import {
  IconCircleCheck,
  IconMapPin,
  IconClock,
  IconDownload,
  IconShoppingBag,
} from "@tabler/icons-react";
import { Image } from "../../../../components/common/image";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { useSaleDetails } from "../../../sales/hooks/useSaleDetails";
import { useEffect, useState } from "react";

const NEXT_STEPS = [
  "You'll receive an order confirmation email with all the details",
  "We'll notify you as each shipment is updated or delivered",
  "Track your order status anytime from your dashboard",
  "Our support team is available 24/7 if you need assistance",
];

interface ConfirmationViewProps {
  saleId: number;
  onContinueShopping: () => void;
}

function formatLongDate(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function addBusinessDays(from: Date, days: number): Date {
  const out = new Date(from);
  let added = 0;
  while (added < days) {
    out.setDate(out.getDate() + 1);
    const day = out.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return out;
}

export function ConfirmationView({ saleId, onContinueShopping }: ConfirmationViewProps) {
  const [shouldPoll, setShouldPoll] = useState(true);
  // Poll while the SO is still draft/sent — the Stripe webhook normally
  // confirms it within a few seconds, but for a brief window after the user
  // lands here it may not have caught up yet. Once `state` flips out of
  // draft/sent we let SWR fall back to its default (no refresh).
  const { data: sale, isLoading } = useSaleDetails(saleId, {
    refreshInterval: shouldPoll ? 2000 : 0,
  });

  const isAwaitingConfirmation =
    !!sale && (sale.state === "draft" || sale.state === "sent");

  useEffect(() => {
    if (sale && !isAwaitingConfirmation) {
      setShouldPoll(false);
    }
  }, [isAwaitingConfirmation, sale]);

  if (isLoading || !sale) {
    return (
      <div className="flex w-full flex-col items-center gap-6 px-8 py-10">
        <ShimmerLoader message="Loading your order…" />
      </div>
    );
  }

  if (isAwaitingConfirmation) {
    return (
      <div className="flex w-full flex-col items-center gap-6 px-8 py-16">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-accentCyanTint">
          <IconClock size={36} className="text-accentCyan700" />
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          <Heading variant="h2" tone="dark">Processing your order…</Heading>
          <Text variant="body" tone="body">
            Your payment was successful. We're finalising your order — this
            usually takes just a few seconds.
          </Text>
          <Text variant="bodySm" tone="muted">
            You'll receive a confirmation email as soon as it's ready.
          </Text>
        </div>
        <ShimmerLoader message="Confirming with our system…" />
      </div>
    );
  }

  const currency = sale.currency?.name ?? "QAR";
  const orderDate = formatLongDate(sale.dateOrder);
  const ship = sale.shippingPartner;

  // Generic 3-7 business-day estimate (Phase 1 — see TODO_IMPROVEMENTS.md
  // for per-supplier shipping policies). Anchored on the order date so the
  // window is stable if the user revisits this page later.
  const orderDateObj = sale.dateOrder ? new Date(sale.dateOrder) : new Date();
  const eta = `${formatLongDate(addBusinessDays(orderDateObj, 3).toISOString())} – ${formatLongDate(addBusinessDays(orderDateObj, 7).toISOString())}`;

  return (
    <div className="flex w-full flex-col items-center gap-6 px-8 py-10">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-accentGreenTintBg">
        <IconCircleCheck size={44} className="text-accentGreen" />
      </div>

      <div className="flex flex-col items-center gap-2 text-center">
        <Heading variant="h2" tone="dark">Order Confirmed!</Heading>
        <Text variant="body" tone="body">
          Thank you for your purchase. Your order has been successfully placed.
        </Text>
      </div>

      <div className="flex flex-col items-center gap-1 rounded-2xl border border-borderDefault bg-bgLight px-10 py-4">
        <Text variant="bodySm" tone="muted">Order Number</Text>
        <Heading variant="h3" tone="dark">{sale.name}</Heading>
      </div>

      <div className="grid w-full max-w-4xl grid-cols-2 gap-4">
        <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgLight p-5">
          <div className="flex items-center gap-2">
            <IconMapPin size={18} className="text-textDark" />
            <Heading variant="h3" tone="dark">Delivery Address</Heading>
          </div>
          <div className="flex flex-col gap-0.5">
            <Text variant="bodyBold" tone="dark">{ship?.name ?? "—"}</Text>
            {ship?.street && <Text variant="body" tone="body">{ship.street}</Text>}
            {ship?.street2 && <Text variant="body" tone="body">{ship.street2}</Text>}
            {(ship?.city || ship?.zip) && (
              <Text variant="body" tone="body">
                {[ship?.zip, ship?.city].filter(Boolean).join(" ")}
              </Text>
            )}
            {ship?.country?.name && (
              <Text variant="body" tone="body">{ship.country.name}</Text>
            )}
            {ship?.phone && (
              <Text variant="body" tone="body">Contact: {ship.phone}</Text>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgLight p-5">
          <div className="flex items-center gap-2">
            <IconClock size={18} className="text-textDark" />
            <Heading variant="h3" tone="dark">Delivery Timeline</Heading>
          </div>
          <div className="flex flex-col gap-3">
            {[
              { label: "Order Placed", detail: orderDate, done: true },
              { label: "Processing", detail: "1-2 business days", done: false },
              { label: "Estimated Delivery", detail: eta, done: false },
            ].map((t) => (
              <div key={t.label} className="flex items-start gap-3">
                <div
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    t.done ? "bg-textDark" : "bg-borderDefault"
                  }`}
                />
                <div>
                  <Text variant="bodyBold" tone="dark">{t.label}</Text>
                  <Text variant="bodySm" tone="muted">{t.detail}</Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full max-w-4xl rounded-2xl border border-borderDefault bg-bgLight p-5">
        <Heading variant="h3" tone="dark" className="mb-4">Order Summary</Heading>
        <div className="flex flex-col gap-4">
          {sale.orderLines.map((line) => (
            <div key={line.id} className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-bgLight">
                  <Image
                    image={line.image ?? null}
                    alt={line.name}
                    size="w-full h-full"
                    showPlaceholder
                  />
                </div>
                <div className="flex flex-1 items-center justify-between gap-2">
                  <div>
                    <Text variant="bodyBold" tone="dark">{line.name}</Text>
                    <Text variant="bodySm" tone="muted">Quantity: {line.quantity}</Text>
                  </div>
                  <CurrencyAmount
                    amount={line.subtotal}
                    currency={currency}
                    variant="bodyBold"
                    tone="dark"
                  />
                </div>
              </div>
              <div className="border-t border-borderDefault" />
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Text variant="body" tone="body">Subtotal</Text>
            <CurrencyAmount amount={sale.amountUntaxed} currency={currency} variant="body" tone="dark" />
          </div>
          <div className="flex items-center justify-between">
            <Text variant="body" tone="body">VAT</Text>
            <CurrencyAmount amount={sale.amountTax} currency={currency} variant="body" tone="dark" />
          </div>
          <div className="flex items-center justify-between">
            <Text variant="body" tone="body">Delivery</Text>
            <Text variant="body" tone="inherit" className="text-accentGreenDark">Included</Text>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Heading variant="h3" tone="dark">Total Paid</Heading>
          <CurrencyAmount amount={sale.amountTotal} currency={currency} variant="h3" tone="dark" />
        </div>
      </div>

      <div className="grid w-full max-w-4xl grid-cols-2 gap-4">
        <Button
          variant="secondary"
          fullWidth
          className="!h-12 !bg-bgLight"
          leftIcon={<IconDownload size={18} />}
          disabled={!sale.previewUrl}
          onClick={() => sale.previewUrl && window.open(sale.previewUrl, "_blank", "noopener")}
        >
          Download Confirmation
        </Button>
        <Button
          variant="secondary"
          fullWidth
          className="!h-12 !bg-bgLight"
          leftIcon={<IconShoppingBag size={18} />}
          onClick={onContinueShopping}
        >
          Continue shopping
        </Button>
      </div>

      <div className="w-full max-w-4xl rounded-2xl bg-accentGreenTintBg p-5">
        <Heading variant="h3" tone="dark" className="mb-3">What happens next?</Heading>
        {sale.dropshipping?.length && sale.dropshipping.length > 1 ? (
          <Text variant="bodySm" tone="body" className="mb-3">
            This order may be split across {sale.dropshipping.length} shipments.
          </Text>
        ) : null}
        <ul className="flex flex-col gap-2">
          {NEXT_STEPS.map((step) => (
            <li key={step}>
              <Text variant="body" tone="body">• {step}</Text>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
